import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material';
import axios from 'axios';
import dayjs from 'dayjs';
import SortButton from './common/SortButton';
import SearchWindow from './common/SearchWindow';
import { isHorizontalIpadMini, isVerticalIpadMini } from '../common/responsiveUtils';
import { SORT_CATEGORIES } from '../common/constants';

import Paper from '@mui/material/Paper';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';

import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';

const TrainingResults = (props) => {
  const { typography, palette } = useTheme();

  const [trainingResultsData, setTrainingResultsData] = useState([]);
  const [searchText, setSearchText] = useState('');

  const styles = {
    csvDownloadButton: {
      ...typography.body14bold,
      height: '44px',
      width: '224px',
      marginLeft: '24px',
      padding: '0px 0px',
      backgroundColor: 'white',
      color: 'primary.main',
      border: '1px solid ' + palette.primary.main,
    },
    editTrainerCheckButton: {
      ...typography.body14bold,
      height: '44px',
      width: '224px',
      marginLeft: 'auto',
      backgroundColor: 'white',
      color: 'primary.main',
      border: '1px solid ' + palette.primary.main,
    },
    editSelfCheckButton: {
      ...typography.body14bold,
      height: '44px',
      width: '224px',
      marginLeft: '24px',
      backgroundColor: 'white',
      color: 'primary.main',
      border: '1px solid ' + palette.primary.main,
    },
  };

  useEffect(() => {
    const fetchTrainingResults = async () => {
      try {
        const result = await axios.get('/v1/training-results');
        setTrainingResultsData(result.data);
      } catch (err) { }
    };
    fetchTrainingResults();
  }, []);

  const handleDownload = async () => {
    try {
      const bom = new Uint8Array([0xef, 0xbb, 0xbf]);
      const downloadLink = document.createElement('a');

      const csv = await axios.get('/v1/training-results/download');
      downloadLink.download = 'trainingResult.csv';
      downloadLink.href = window.URL.createObjectURL(new Blob([bom, csv.data.trainingResult], { type: 'text/csv' }));
      downloadLink.click();

      downloadLink.download = 'operationLog.csv';
      downloadLink.href = window.URL.createObjectURL(new Blob([bom, csv.data.operationLog], { type: 'text/csv' }));
      downloadLink.click();

      downloadLink.remove();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <Container>
      <Stack direction='row' sx={{ padding: '8px 0px' }}>
        <Button variant="outlined"
          sx={styles.editTrainerCheckButton}
          startIcon={<EditOutlinedIcon />}
          onClick={() => {
            props.handleMoveToEditTrainerCheck();
          }}
        >
          指導者チェック項目の編集
        </Button>
        <Button variant="outlined"
          sx={styles.editSelfCheckButton}
          startIcon={<EditOutlinedIcon />}
          onClick={() => {
            props.handleMoveToEditSelfCheck();
          }}
        >
          セルフチェック問題の編集
        </Button>
      </Stack>
      <Stack direction='row' sx={{ padding: '8px 0px' }}>
        <SearchWindow
          searchText={searchText}
          setSearchText={setSearchText}
        />
        <SortButton
          items={[
            SORT_CATEGORIES.CORRECT_ANSWER_DESC,
            SORT_CATEGORIES.CORRECT_ANSWER_ASC,
            SORT_CATEGORIES.EMPLOYEE_NUMBER_ASC,
            SORT_CATEGORIES.EMPLOYEE_NAME_ASC,
            SORT_CATEGORIES.TRAINED_ASC,
            SORT_CATEGORIES.TRAINED_DESC,
          ]}
          initialItem={SORT_CATEGORIES.TRAINED_DESC}
          tableData={trainingResultsData}
          setTableData={setTrainingResultsData}
        />
        <Button variant="outlined"
          sx={styles.csvDownloadButton}
          startIcon={<CloudDownloadOutlinedIcon />}
          onClick={() => {
            handleDownload();
          }}
        >
          すべてのデータをcsv出力
        </Button>
      </Stack>
      <TrainingResultTable
        trainingResultsData={trainingResultsData.filter((e) => {
          // 入力文字列の変化に合わせて表示内容を絞り込む
          return (e.driver.employeeNumber + e.driver.name).includes(searchText);
        })}
        handleMoveToDetailTrainingResult={props.handleMoveToDetailTrainingResult}
      />
    </Container>
  );
};

// トレーニング結果表示用テーブル
const TrainingResultTable = (props) => {
  const { typography, palette } = useTheme();
  const isVerticallyLong = isVerticalIpadMini();
  const font = isVerticallyLong ? 'caption13bold' : 'body14bold';
  const maxCellWidth = isVerticallyLong ? '145px' : null;
  // テーブルの高さをPC, iPadMini(縦), iPadMini(横)で分ける
  let maxTableHeight = '490px';
  if (isVerticallyLong) maxTableHeight = '690px';

  if (isHorizontalIpadMini()) maxTableHeight = '400px';

  const styles = {
    tableCellHead: {
      ...typography[font],
      color: palette.darkgray,
    },
    tableCellBody: {
      ...typography.body14,
      maxWidth: maxCellWidth,
    },
    noTrainingResultMessage: {
      ...typography.body16bold,
      color: palette.darkgray,
      align: 'center',
    },
  };

  // APIのレスポンスデータを表に表示する形式に加工
  const rows = props.trainingResultsData.map((e) => {
    const sCheckResult = e.selfCheckResult;
    const tCheckResult = e.trainerCheckResult;
    return {
      _id: e._id,
      driverEmployeeNumber: e.driver.employeeNumber,
      driverName: e.driver.name,
      trainingTime: `${dayjs(e.startedAt).format('MM月DD日HH時mm分')} ~ ${dayjs(e.finishedAt).format('HH時mm分')}`,
      selfCheck: (sCheckResult === null) ? '-' : 'あり',
      correctAnswerCount: (sCheckResult === null) ? '-' : `${sCheckResult.correctAnswerCount}/${sCheckResult.itemCount}問`,
      trainer: (e.trainer === null) ? '-' : e.trainer.name,
      checkedItemCount: (tCheckResult === null) ? '-' : `${tCheckResult.checkedItemCount}/${tCheckResult.itemCount}点`,
    };
  });

  const handleSelect = (_id) => {
    props.handleMoveToDetailTrainingResult(_id);
  };

  return (
    <TableContainer component={Paper} sx={{ maxHeight: maxTableHeight }}>
      <Table aria-label="simple table" >
        <TableHead sx={{ background: palette.backgroundBlue, position: 'sticky', top: 0 }}>
          <TableRow>
            <TableCell sx={styles.tableCellHead}>受講アカウント</TableCell>
            <TableCell sx={styles.tableCellHead}>受講日時</TableCell>
            <TableCell sx={styles.tableCellHead}>セルフチェック</TableCell>
            <TableCell sx={styles.tableCellHead}>正答数</TableCell>
            <TableCell sx={styles.tableCellHead}>指導者確認</TableCell>
            <TableCell sx={styles.tableCellHead}>チェック数</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} align='center' height='445px'>
                <Typography sx={styles.noTrainingResultMessage}>まだトレーニング結果が登録されていません</Typography>
              </TableCell>
            </TableRow>
          )}
          {rows.map((row) => (
            <TableRow
              hover
              key={row._id}
              onClick={() => handleSelect(row._id)}
            >
              <TableCell sx={styles.tableCellBody} component="th">
                {row.driverEmployeeNumber}<br />{row.driverName}
              </TableCell>
              <TableCell sx={styles.tableCellBody}>{row.trainingTime}</TableCell>
              <TableCell sx={styles.tableCellBody}>{row.selfCheck}</TableCell>
              <TableCell sx={styles.tableCellBody}>{row.correctAnswerCount}</TableCell>
              <TableCell sx={styles.tableCellBody}>{row.trainer}</TableCell>
              <TableCell sx={styles.tableCellBody}>
                {row.checkedItemCount}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default TrainingResults;

TrainingResults.propTypes = {
  loginUser: PropTypes.object,
  handleMoveToDetailTrainingResult: PropTypes.func,
  handleMoveToEditTrainerCheck: PropTypes.func,
  handleMoveToEditSelfCheck: PropTypes.func,
};

TrainingResultTable.propTypes = {
  trainingResultsData: PropTypes.array,
  handleMoveToDetailTrainingResult: PropTypes.func,
};
